// useMessageArea — where messages may appear (pen "Messages" §1): the main
// area only, the space between the menu (and the Studio's tool bar) and the
// chat panel. Toasts sit at its bottom centre, 24 above the bottom row (the
// Studio's zoom pill), so they never cover a panel, the composer, the menu or
// the bottom row; when the chat is minimised the area widens and they
// re-centre. The last-step line sits right of the bottom row.
//
// Shell edge: it reads the laid-out DOM (the AppShell's centre column and the
// Studio's chrome) and keeps the result as view-ephemeral state. Re-measured
// when the centre resizes, the window resizes, or `watch` changes (the active
// view, the number of messages showing).

export { useMessageArea };

import { useLayoutEffect, useState } from "react";

// Pixel offsets in viewport coordinates, as CSS `left` / `right` / `bottom`.
type MessageArea = {
    readonly left: number;
    readonly right: number;
    readonly bottom: number;
    // The last-step line: right of the bottom row, centred on it. Without a
    // bottom row it sits at the area's bottom left.
    readonly lineLeft: number;
    readonly lineBottom: number;
};

// The pieces of chrome the area keeps clear of.
const CENTRE = ".bilda-app-shell__centre";
const SIDE_TOOLBAR = ".bilda-studio-toolbar";
const BOTTOM_ROW = ".bilda-studio__zoom";

const ABOVE_ROW = 24;
const BESIDE_ROW = 8;
const EDGE = 16;
// The last-step line's height.
const LINE = 28;

const measure = (): MessageArea | null => {
    const centre = document.querySelector(CENTRE);
    if (centre === null) return null;
    const c = centre.getBoundingClientRect();
    const toolbar = centre.querySelector(SIDE_TOOLBAR)?.getBoundingClientRect();
    const row = centre.querySelector(BOTTOM_ROW)?.getBoundingClientRect();
    const height = window.innerHeight;
    const left = Math.max(c.left, toolbar?.right ?? c.left);
    return {
        left: Math.round(left),
        right: Math.round(window.innerWidth - c.right),
        bottom: Math.round(height - (row?.top ?? c.bottom) + ABOVE_ROW),
        lineLeft: Math.round(
            row === undefined ? left + EDGE : row.right + BESIDE_ROW,
        ),
        lineBottom: Math.round(
            row === undefined
                ? height - c.bottom + EDGE
                : height - row.bottom + (row.height - LINE) / 2,
        ),
    };
};

const same = (a: MessageArea | null, b: MessageArea | null): boolean =>
    a === b ||
    (a !== null &&
        b !== null &&
        a.left === b.left &&
        a.right === b.right &&
        a.bottom === b.bottom &&
        a.lineLeft === b.lineLeft &&
        a.lineBottom === b.lineBottom);

const useMessageArea = (watch: string): MessageArea | null => {
    const [area, setArea] = useState<MessageArea | null>(null);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `watch` is the re-measure trigger (the DOM it summarises is not React state).
    useLayoutEffect(() => {
        const update = () => {
            const next = measure();
            setArea((prev) => (same(prev, next) ? prev : next));
        };
        update();
        const observer = new ResizeObserver(update);
        const centre = document.querySelector(CENTRE);
        if (centre !== null) observer.observe(centre);
        window.addEventListener("resize", update);
        return () => {
            observer.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [watch]);
    return area;
};
